// What the submission flow tells Sentry when a call fails (added 2026-10-08).
//
// The page calls three APIs: Registration, ProfileManagement (photos, resumes, agents) and the
// actor phone API. Registration logs its own side; the other two never reach it, so the browser
// is the only place their failures show. Every failed call leaves a breadcrumb, which travels
// with any later Sentry event from the tab. The unexpected ones become an event of their own:
// no answer (network), a server error, the session lost mid-flow, or an error with no code.
// The rest (a wrong code, a closed role, a file too large) are the flow working as designed.
//
// Never the arguments or the response body: they hold emails, codes, phone numbers and notes.
// Only the method, the status, the error code and the account id.

import * as Sentry from "@sentry/react";
import { getSubmissionAuth } from "../../../helpers/submissionAuth";
import { ISubmissionFlowApi } from "./types";

const UNEXPECTED_STATUS = (status: number) => status === 0 || status >= 500 || status === 401 || status === 403;

export const reportApiFailure = (method: string, err: any) => {
  try {
    const status = typeof err?.status === "number" ? err.status : 0;
    const errorCode: string = err?.errorCode || "unknown";
    const unexpected = UNEXPECTED_STATUS(status) || errorCode === "unknown";
    Sentry.addBreadcrumb({
      category: "submission-api",
      level: unexpected ? Sentry.Severity.Error : Sentry.Severity.Warning,
      message: `${method} ${status} ${errorCode}`,
    });
    if (!unexpected) return;
    Sentry.withScope((scope) => {
      scope.setLevel(Sentry.Severity.Error);
      scope.setTag("flow", "submission");
      scope.setTag("api.method", method);
      scope.setTag("api.status", String(status));
      scope.setTag("api.errorCode", errorCode);
      const auth = getSubmissionAuth();
      if (auth && auth.clientId) scope.setTag("clientId", String(auth.clientId));
      Sentry.captureMessage(`Submission API ${method} failed: ${status} ${errorCode}`);
    });
  } catch (e) {
    // Reporting must never break the page.
  }
};

/** The same API, with every rejected call reported before it reaches the page. */
export const withErrorReporting = (api: ISubmissionFlowApi): ISubmissionFlowApi => {
  const wrapped: any = {};
  Object.keys(api).forEach((name) => {
    const member = (api as any)[name];
    if (typeof member !== "function") {
      wrapped[name] = member;
      return;
    }
    wrapped[name] = (...args: any[]) => {
      let result: any;
      try {
        result = member.apply(api, args);
      } catch (e) {
        reportApiFailure(name, e);
        throw e;
      }
      if (result && typeof result.then === "function") {
        return result.catch((e: any) => {
          reportApiFailure(name, e);
          throw e;
        });
      }
      return result;
    };
  });
  return wrapped as ISubmissionFlowApi;
};
